import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column, Divider } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { UpdateCapitalActionFormValues } from '../../hooks/useCapitalActionForm';

type Props = {
  vehicleId: string;
  index: number;
  baseCurrency: CurrencyCode;
  availableActions: Array<{
    id: string;
    ledgerDate: CalendarDay;
    uncalledAmountLcy: number;
  }>;
};

const PartnerTransferDetails: React.FC<Props> = ({
  vehicleId,
  index,
  availableActions,
  baseCurrency,
}) => {
  const { id } = useParams<{ id?: string }>();
  const { control, watch, setValue } =
    useFormContext<UpdateCapitalActionFormValues>();

  const partnerTransferId = watch(`entries.${index}.partnerTransferId`);

  const { data: actionsDetails } = api.capitalAction.getActionsDetails.useQuery(
    {
      capitalActionId: id,
      vehicleId,
      entryType: CapitalActionEntryType.PartnerTransfer,
      actionIds: [partnerTransferId ?? ''],
    },
    {
      enabled: !!partnerTransferId,
    },
  );

  const partnerTransfer =
    actionsDetails?.entryType === CapitalActionEntryType.PartnerTransfer
      ? actionsDetails.actions.find((action) => action.id === partnerTransferId)
      : undefined;

  const rows = (partnerTransfer?.investorsBreakdown ?? []).map((row) => ({
    id: row.commitmentId,
    ...row,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    { label: 'Investor', key: 'investorName', width: 250 },
    {
      label: 'Expenses',
      key: 'expenses',
      render: (row) =>
        numberToCurrencyString(row.expenses, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Investments',
      key: 'investments',
      render: (row) =>
        numberToCurrencyString(row.investments, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Fees',
      key: 'fees',
      render: (row) =>
        numberToCurrencyString(row.fees, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Refunds',
      key: 'returnOfCapital',
      render: (row) =>
        numberToCurrencyString(-row.returnOfCapital || 0, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Working capital',
      key: 'workingCapital',
      render: (row) =>
        numberToCurrencyString(row.workingCapital, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Advance subscription called',
      key: 'advanceSubscriptionCalled',
      render: (row) =>
        numberToCurrencyString(row.advanceSubscriptionCalled, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
      withVerticalBorder: true,
    },
    {
      label: 'Net call/(distribution)',
      key: 'netCallOrDistribution',
      render: (row) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Capital contribution used',
      key: 'capitalContributionUsed',
      render: (row) =>
        numberToCurrencyString(row.capitalContributionUsed, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      label: 'Advance subscription used',
      key: 'advanceSubscriptionUsed',
      render: (row) =>
        numberToCurrencyString(row.advanceSubscriptionUsed, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  const handleSelectPartnerTransfer = (partnerTransferId: string) => {
    const partnerTransfer = availableActions.find(
      (action) => action.id === partnerTransferId,
    );
    if (partnerTransfer) {
      setValue(`entries.${index}.totalAmountLcy`, 0);
    }
  };

  return (
    <Column spacing="lg">
      <FormField label="Select partner transfer" size="md">
        <Controller
          name={`entries.${index}.partnerTransferId`}
          control={control}
          render={({
            fieldState: { error },
            field: { onChange, ...field },
          }) => (
            <BasicSelect
              placeholder="Select partner transfer"
              fullWidth
              options={availableActions.map((action) => ({
                label: `Partner transfer - ${action.ledgerDate.toReadable()}`,
                value: action.id,
              }))}
              onChange={(value) => {
                handleSelectPartnerTransfer(value);
                onChange(value);
              }}
              {...field}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      {!!partnerTransferId && <Divider />}
      {!!partnerTransferId && (
        <Table rows={rows} columns={columns} scrollable disableSorting />
      )}
    </Column>
  );
};

export default PartnerTransferDetails;
